import { Schema, SchemaIssue } from "effect";
import { schemaDecoder } from "./schema-compat.ts";

const required = <S extends Schema.Top>(schema: S, expected: string) =>
  schema.annotate({ identifier: expected }).annotateKey({ messageMissingKey: `Expected ${expected}` });
const Text = /* @__PURE__ */ (() => required(Schema.String, "a string"))();
const NonNegative = /* @__PURE__ */ (() => required(Schema.Number.check(
  Schema.isFinite({ expected: "a non-negative finite number" }),
  Schema.isGreaterThanOrEqualTo(0, { expected: "a non-negative finite number" })
), "a non-negative finite number"))();
const integer = (minimum: number) => {
  const expected = `an integer greater than or equal to ${minimum}`;
  return required(Schema.Number.check(Schema.isInt({ expected }), Schema.isGreaterThanOrEqualTo(minimum, { expected })), expected);
};
const Count = /* @__PURE__ */ (() => integer(0))();
const PositiveCount = /* @__PURE__ */ (() => integer(1))();


export const TestStatus = /* @__PURE__ */ (() => required(Schema.Literals(["passed", "failed", "skipped", "todo"]), "passed, failed, skipped, or todo"))();
export type TestStatus = typeof TestStatus.Type;

export const TestLogEntry = /* @__PURE__ */ (() => Schema.Struct({
  level: Text.pipe(Schema.mutableKey),
  message: Text.pipe(Schema.mutableKey),
  timestamp: NonNegative.pipe(Schema.mutableKey),
}))();
export type TestLogEntry = typeof TestLogEntry.Type;

export const TestRunError = /* @__PURE__ */ (() => Schema.Struct({
  name: Text.pipe(Schema.mutableKey),
  message: Text.pipe(Schema.mutableKey),
  stack: Text.pipe(Schema.optional, Schema.mutableKey),
}))();
export type TestRunError = typeof TestRunError.Type;

export const TestResultItem = /* @__PURE__ */ (() => Schema.Struct({
  file: Text.pipe(Schema.mutableKey),
  name: Text.pipe(Schema.mutableKey),
  fullName: Text.pipe(Schema.mutableKey),
  status: TestStatus.pipe(Schema.mutableKey),
  duration: NonNegative.pipe(Schema.mutableKey),
  logs: required(Schema.Array(TestLogEntry).pipe(Schema.mutable), "an array of log entries").pipe(Schema.mutableKey),
  error: TestRunError.pipe(Schema.optional, Schema.mutableKey),
}))();
export type TestResultItem = typeof TestResultItem.Type;

export const TestRunSummary = /* @__PURE__ */ (() => Schema.Struct({
  total: Count.pipe(Schema.mutableKey),
  passed: Count.pipe(Schema.mutableKey),
  failed: Count.pipe(Schema.mutableKey),
  skipped: Count.pipe(Schema.mutableKey),
  todo: Count.pipe(Schema.mutableKey),
  duration: NonNegative.pipe(Schema.mutableKey),
}))();
export type TestRunSummary = typeof TestRunSummary.Type;

/** A flow recording captured while a hosted test drove a browser session. */
export const TestRunFlowRecording = /* @__PURE__ */ (() => Schema.Struct({
  recordingId: Text.pipe(Schema.mutableKey),
  /** The recording may live under an ephemeral hosted-test scope. */
  workspaceId: Text.pipe(Schema.mutableKey),
  flowName: Text.pipe(Schema.mutableKey),
  mode: Text.pipe(Schema.mutableKey),
  state: Text.pipe(Schema.mutableKey),
  startedAt: NonNegative.pipe(Schema.mutableKey),
  durationMs: NonNegative.pipe(Schema.optional, Schema.mutableKey),
}))();
export type TestRunFlowRecording = typeof TestRunFlowRecording.Type;

/** The completed hosted-test payload shared by server and UI boundaries. */
export const TestRunResult = /* @__PURE__ */ (() => required(Schema.Struct({
  version: PositiveCount.pipe(Schema.mutableKey),
  files: required(Schema.Array(Text).pipe(Schema.mutable), "an array of strings").pipe(Schema.mutableKey),
  concurrency: PositiveCount.pipe(Schema.mutableKey),
  testFiles: required(Schema.Array(Text).pipe(Schema.mutable), "an array of strings").pipe(Schema.mutableKey),
  tests: required(Schema.Array(TestResultItem).pipe(Schema.mutable), "an array").pipe(Schema.mutableKey),
  summary: TestRunSummary.pipe(Schema.mutableKey),
  fatal: TestRunError.pipe(Schema.optional, Schema.mutableKey),
  logs: required(Schema.Array(TestLogEntry).pipe(Schema.mutable), "an array of log entries").pipe(Schema.mutableKey),
  flowRecordings: required(Schema.Array(TestRunFlowRecording).pipe(Schema.mutable), "an array").pipe(Schema.optional, Schema.mutableKey),
  bundleInfo: Schema.Struct({
  mainModule: Text.pipe(Schema.mutableKey),
  modules: required(Schema.Array(Text).pipe(Schema.mutable), "an array of strings").pipe(Schema.mutableKey),
  warnings: required(Schema.Array(Text).pipe(Schema.mutable), "an array of strings").pipe(Schema.mutableKey),
}).pipe(Schema.mutableKey),
  timing: Schema.Struct({
  buildTime: NonNegative.pipe(Schema.mutableKey),
  runTime: NonNegative.pipe(Schema.mutableKey),
  totalTime: NonNegative.pipe(Schema.mutableKey),
}).pipe(Schema.mutableKey),
}), "an object"))();
export type TestRunResult = typeof TestRunResult.Type;


const ResultErrorFields = {
  path: Schema.String, expectation: Schema.String, message: Schema.String,
  status: Schema.tag(500)
};
export class HostedTestResultContractError extends Schema.TaggedError<HostedTestResultContractError>()(
  "HostedTestResultContractError", ResultErrorFields
) {
  constructor(path: string, expectation: string);
  constructor(properties: Schema.Struct<typeof ResultErrorFields>["~type.make.in"], options?: Schema.MakeOptions);
  constructor(path: string | Schema.Struct<typeof ResultErrorFields>["~type.make.in"], expectation?: string | Schema.MakeOptions) {
    super(typeof path === "string" ? {
      path, expectation: expectation as string,
      message: `Hosted-test result ${path} must be ${expectation}.`
    } : path, typeof expectation === "object" ? expectation : undefined);
  }
}

const formatIssue = /* @__PURE__ */ (() => SchemaIssue.makeFormatterStandardSchemaV1())();
const resultContract = /* @__PURE__ */ (() => schemaDecoder(TestRunResult, error => {
  const first = formatIssue(error.issue).issues[0];
  const path = first?.path?.reduce<string>((text, segment) => {
    const key = typeof segment === "object" ? segment.key : segment;
    return typeof key === "number" ? `${text}[${key}]` : `${text}${text ? "." : ""}${String(key)}`;
  }, "") || "payload";
  const expectation = (first?.message ?? "valid").replace(/^Expected /, "");
  return new HostedTestResultContractError(path, expectation);
}))();

/** Decode the JSON serialization boundary before rendering a hosted-test result. */
export const parseHostedTestRunResult = resultContract.parse;
